"use client"

import React from "react"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { useTranslations, useLocale } from 'next-intl'

export function NewsletterSubscribe({ className }: { className?: string }) {
  const locale = useLocale()
  const t = useTranslations('footer.subscribe')
  const [email, setEmail] = React.useState('')
  const [statusMsg, setStatusMsg] = React.useState<string | null>(null)
  const [statusType, setStatusType] = React.useState<'idle'|'success'|'error'>('idle')
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  const handleSubscribe = async () => {
    if (isSubmitting) return
    setStatusType('idle')
    setStatusMsg(null)
    let normalized = email.trim()
    try {
      if ((normalized as any).normalize) normalized = (normalized as any).normalize('NFC')
    } catch {}
    normalized = normalized.toLowerCase()
    const re = /^[^@\s]+@[^@\s]+\.[^@\s]+$/
    if (!re.test(normalized)) {
      setStatusType('error')
      setStatusMsg(t('status.invalid'))
      return
    }
    setIsSubmitting(true)
    try {
      const res = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: normalized, locale })
      })
      let data: any = null
      try {
        data = await res.json()
      } catch {
        try {
          const txt = await res.text()
          data = { __raw: txt }
        } catch {}
      }
      if (res.status === 409 || data?.code === 'ALREADY_SUBSCRIBED') {
        // Treat as success (idempotent) so the UI is friendly
        setStatusType('success')
        setStatusMsg(t('status.already'))
        setEmail('')
      } else if (!res.ok || data?.success !== true) {
        const err = data?.error || data?.__raw || t('status.error')
        setStatusType('error')
        setStatusMsg(err)
      } else {
        setStatusType('success')
        setStatusMsg(t('status.success'))
        setEmail('')
      }
    } catch {
      setStatusType('error')
      setStatusMsg(t('status.network'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className={className}>
      <h3 className="text-lg font-semibold mb-2">{t('title') || 'Stay Updated'}</h3>
      <p className="text-sm text-medium-contrast mb-4">{t('description') || 'Get the latest articles delivered to your inbox.'}</p>
      <div className="flex w-full gap-2 mb-2">
        <Input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder={t('placeholder')}
          className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-400 focus:border-sky-400"
        />
        <Button
          onClick={handleSubscribe}
          disabled={isSubmitting}
          className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700"
        >
          {isSubmitting ? `${t('button')}…` : t('button')}
        </Button>
      </div>
      {statusMsg && (
        <p className={`text-sm mt-2 ${statusType === 'success' ? 'text-green-500' : 'text-red-400'}`} role="status" aria-live="polite">
          {statusMsg}
        </p>
      )}
    </div>
  )
}
